<template>
  <div class="container py-5">
    <div class="level">
      <button
        :disabled="!valid || saving"
        @click="addNewRequest()"
        :class="{ 'is-loading': saving }"
        class="level-left button is-primary mb-0">
        Submit Sample Request
      </button>
      <v-switcher 
        v-model="request.needs_nda" 
        label="Request Needs NDA"/>
    </div>
    
    <v-form 
      :key="`${request.request_reason}-${request.needs_nda}`"
      :valid.sync="valid" 
      class="columns is-multiline"
    >
      <div class="column is-one-third" :key="request.request_reason">

        <div class="field">
          <label class="label">Request Reason*</label>
          <v-select
            required
            :options="requestReasons"
            v-model="request.request_reason"
          />
        </div>

        <div class="field">
          <label class="label">Account*</label>
          <account-search 
            required
            v-model="request.account_id"
          />
        </div>

        <div class="field">
          <label class="label">Contact*</label>
          <contact-search
            required
            :account-id="request.account_id"
            v-model="request.contact_id"/>
        </div>

        <v-fade-transition 
          tag="div"
          class="field"
          mode="out-in">
          <div
            v-if="request.request_reason == 'Project'">
            <label class="label">Project*</label>
            <search-autocomplete
              required
              v-model="sample.project_id"
            />
          </div>
        </v-fade-transition>

        <div class="field">
          <label class="label">Expected By*</label>
          <v-datepicker
            required
            v-model="request.expected_date_needed"
          />
        </div>

        <div class="field">
          <label class="label">Requested By*</label>
          <v-select
            required
            v-if="users.length > 0"
            :options="users.filter(
              d => (['sales', 'ceo', 'sales-estimator', 'developer', 'sales-estimator-samples'].includes(d.role) || 
                d.permission == 'admin') && d.is_active == true)"
            item-text="display_name"
            item-value="id"
            v-model="request.requested_by_id"
          />
        </div>

        <div class="field">
          <label class="label">Requested To*</label>
          <v-select
            required
            v-if="users.length > 0"
            :options="users.filter(d => ['office-manager', 'sales-estimator-samples'].includes(d.role))"
            item-text="display_name"
            item-value="id"
            v-model="request.requested_to_id"
          />
        </div>

      </div>

      <div class="column is-two-thirds">

        <div class="columns is-multiline">

          <div 
            class="column is-12"
            v-if="!request.request_reason">
            <article 
              class="message is-info is-fullwidth">
              <div class="message-body">
                Please select a request reason to select the sample needed.
              </div>
            </article>
          </div>

          <template v-else>

            <template v-if="!isIntroduction">
              <div class="column is-one-quarter">
                <label class="label">Sample Product*</label>
                <product-search
                  required
                  :product-category-id="13"
                  v-model="sample.product_master_id"
                />        
              </div>

              <div class="column is-one-quarter">
                <label class="label">Sample Color</label>
                <v-text-field
                  v-model="sample.color">
                </v-text-field>
              </div>

              <div class="column is-one-quarter">
                <label class="label">Sample Pattern</label>
                <v-text-field
                  v-model="sample.pattern">
                </v-text-field>
              </div>
            </template>  

            <template v-else>
              <div 
                class="column is-three-quarters">
                <label class="label">Sample*</label>
                <v-select 
                  :options="samples"
                  :item-text="v => 
                    `${v.product_master.name}-${v.color}-${v.pattern}`"
                  item-value="id"
                  v-model="request.sample_id"
                />
              </div>
            </template>

            <div class="column is-one-quarter">
              <label class="label">Sample Quantity*</label>
              <v-text-field
                required
                type="number"
                placeholder="Sample Quantity"
                v-model="request.qty_to_send">
              </v-text-field>
            </div>

            <template v-if="!isIntroduction">
              <div class="column is-one-third">
                <label class="label">Type of Glass*</label>
                <v-text-field
                  required
                  v-model="sample.glass_type"
                  placeholder="Type of Glass"
                />
              </div>

              <div class="column is-one-third">
                <label class="label">Power Outcome (W/SQFT)*</label>
                <v-text-field
                  required
                  v-model="sample.power_outcome"
                  type="number"
                  placeholder="Power Outcome" 
                />
              </div>

              <div class="column is-one-quarter">
                <label class="label">Active or Inactive</label>
                <v-radio-button 
                  :options="[
                    { label: 'Active', value: 'active' }, 
                    { label: 'Inactive', value: 'inactive' }
                  ]"
                  v-model="sample.active_inactive"
                />
              </div>
            </template>  

            <div class="column is-full">
              <label class="label">Custom Sample Image</label>
              <v-drop-zone
                height="120px"
                id='custom-sample-image'
                :file-name.sync="customSample.name"
                v-model="customSample.file">
              </v-drop-zone>
              <div class="help">
                Upload an image for the sample if it is a custom request
              </div>
            </div>

          </template>

          <div class="column is-4">
            <label class="label">Shipping Address*</label>
            <v-text-field
              required
              placeholder="Shipping Address"
              v-model="request.ship_to_address"
            />
          </div>

          <div class="column is-2">
            <label class="label">City*</label>
            <v-text-field
              required
              placeholder="Shipping City"
              v-model="request.ship_to_city"
            />
          </div>

          <div class="column is-2">
            <label class="label">Province/State*</label>
            <v-text-field
              required
              placeholder="Shipping Province/ State"
              v-model="request.ship_to_province"
            />
          </div>

          <div class="column is-2">
            <label class="label">Postal Code*</label>
            <v-text-field
              required
              placeholder="Shipping Postal Code"
              v-model="request.ship_to_postalcode"
            />
          </div>

          <div class="column is-2">
            <label class="label">Country*</label>
            <v-text-field
              required
              placeholder="Shipping Country"
              v-model="request.ship_to_country"
            />
          </div>

          <div class="column is-6">
            <label class="label">Contact Phone Number*</label>
            <v-text-field
              required
              placeholder="Contact Phone Number"
              v-model="request.ship_to_phone"
            />
          </div>

          <div class="column is-6">
            <label class="label">Unit Number</label>
            <v-text-field
              placeholder="Shipping Unit Number"
              v-model="request.ship_to_unit_number"
            />
          </div>

          <div class="column is-12" v-if="request.needs_nda">
            <label class="label">Sample Request NDA*</label>
            <v-drop-zone
              required
              height="120px"
              id='sample-request-nda'
              :file-name.sync="upload.name"
              v-model="upload.file"
            />
          </div>

          <div class="column is-12">
            <label class="label">Request Comments</label>
            <v-rich-text
              v-model="request.comments"
              min-height="100px">
            </v-rich-text>
          </div>
        </div>

      </div>
    </v-form>

  </div>
</template>

<script>
import VTextField from '@/components/base/VTextField'
import VSelect from '@/components/base/VSelect'
import VForm from '@/components/base/VForm'
import VRichText from '@/components/base/VRichText'
import VDropZone from '@/components/base/VDropZone'
import VDatepicker from '@/components/base/VDatepicker'
import VSwitcher from '@/components/base/VSwitcher'
import VRadioButton from '@/components/base/VRadioButton'
import VFadeTransition from '@/components/transitions/VFadeTransition'

import ProductSearch from '@/components/inventory/ProductSearch'
import SearchAutocomplete from '@/components/projects/SearchAutocomplete'
import AccountSearch from '@/components/crm/AccountSearch'
import ContactSearch from '@/components/crm/ContactSearch'

import { mapState, mapActions, mapMutations } from 'vuex'

export default {
  components: { 
    VTextField, VSelect, VForm, VRichText, VFadeTransition,
    VDatepicker, VDropZone, VSwitcher, VRadioButton, SearchAutocomplete, ProductSearch,
    AccountSearch, ContactSearch
  },
  data() {
    return {
      accounts: [],
      contacts: [],
      request: {
        sample_id: null,
        account_id: null,
        contact_id: null,
        request_reason: null,
        expected_date_needed: null,
        requested_by_id: null,
        requested_to_id: null,
        qty_to_send: null,
        needs_nda: false
      },
      sample: {
        product_master_id: null,
        color: null,
        pattern: null,
        project_id: null,
      },
      customSample: {
        file: null,
        name: null
      },
      upload: {
        file: null,
        name: null
      },
      valid: false,
      saving: false
    }
  },
  computed: {
    ...mapState("users", ["users"]),
    ...mapState("auth", ["user"]),
    ...mapState("samples", ["samples"]),
    ...mapState("sampleRequests", ["requestReasons"]),
    selectedSample({ samples, sample }) {
      return sample.sample_id ? 
        samples.filter(d => d.id == sample.id) : 
        null;
    },
    isIntroduction({ request }) {
      return request.request_reason == 'Introduction';
    }
  },
  watch: {
    'request.request_reason'(v) {
      if (v == 'Introduction') {
        this.getSamples({ per_page: 1000, sample_type: v });
      }
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    ...mapActions("sampleRequests", [
      "addRequest", "addAttachment"
    ]),
    ...mapActions("samples", [
      "getSamples", "addSample", "addSampleImage"
    ]),
    ...mapActions("users", ["getUsers"]),
    ...mapMutations("app", ["setNotification"]),
    initialize() {
      this.request.requested_by_id = this.user.id;
      this.getUsers({ per_page: 1000, is_active: true });
    },
    addNewRequest() {
      this.saving = true;

      if (this.isIntroduction) {
        this.addRequest(this.request).then(resp => {
          if (this.upload.file) {
            this.saving = false;
            this.attach(resp.data.id).then(() => {
              this.notifySuccess();
            }).catch(() => this.saving = false);
          } else {
            this.notifySuccess();
          }
        }).catch(() => this.saving = false);
      } else {
        this.sample.sample_type = this.request.request_reason;
        this.addSample(this.sample).then(resp => {
          if (this.customSample.file) {
            let formData = new FormData();
            formData.append('image', this.customSample.file, this.customSample.name);
            this.addSampleImage({ sampleId: resp.data.id, data: formData });
          }
          this.request.sample_id = resp.data.id;
          this.addRequest(this.request).then(resp => {
            if (this.upload.file) {
              this.attach(resp.data.id).then(() => {
                this.notifySuccess();
              }).catch(() => this.saving = false);
            } else {
              this.notifySuccess();
            }
          }).catch(() => this.saving = false);
        }).catch(() => this.saving = false);
      }
    },
    notifySuccess() {
      this.saving = false;
      this.setNotification({
        status: true,
        type: 'success',
        message: 'Sample successfully requested'
      });
      this.goToList();
    },
    attach(requestId) {
      return new Promise((resolve, reject) => {
        let formData = new FormData();
        formData.append('attachment', this.upload.file, this.upload.file.name);

        this.addAttachment(
          { sampleRequestId: requestId, data: formData }
        ).then(
          resp => resolve(resp)
        ).catch(err => reject(err));
      })
    },
    goToList() {
      this.$router.push({ name: 'SampleRequests' });
    }
  }
}
</script>